import { useAuth } from "@/hooks/useAuth";
import { useBridgetteWorkspace } from "@/hooks/useBridgetteWorkspace";
import { useBridgetteActionExecutor } from "@/hooks/useBridgetteActionExecutor";
import { BridgetteApprovalDialog } from "@/components/bridgette/BridgetteApprovalDialog";
import { CommandBar } from "@/components/bridgette/canvas/CommandBar";
import { CanvasShell } from "@/components/bridgette/canvas/CanvasShell";
import { HomeCanvas } from "@/components/bridgette/canvas/HomeCanvas";
import { ConversationPane } from "@/components/bridgette/workspace/ConversationPane";
import { WorkspaceReviewCanvas } from "@/components/bridgette/workspace/WorkspaceReviewCanvas";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BridgetteWorkspaceApproval } from "@/lib/bridgetteWorkspaceActions";

type CanvasView = "home" | "conversation";

export default function BridgetteWorkspace() {
  const { user, orgId, loading } = useAuth();
  const userId = user?.id ?? null;
  const { toast } = useToast();
  const ws = useBridgetteWorkspace(orgId, userId);
  const { execute, pendingApproval, approve, cancel } = useBridgetteActionExecutor();
  const [view, setView] = useState<CanvasView>("conversation");
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const selectedFile = useMemo(
    () => ws.files.find((file) => file.id === selectedFileId) ?? ws.files[0] ?? null,
    [ws.files, selectedFileId],
  );

  useEffect(() => {
    if (ws.error) toast({ title: "Workspace error", description: ws.error, variant: "destructive" });
  }, [ws.error, toast]);

  const handleCommand = (text: string) => {
    setView("conversation");
    ws.sendMessage(text);
  };

  if (loading || ws.threadInitializing) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-background text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-primary" />
        Preparing your workspace…
      </div>
    );
  }

  if (!user || !orgId) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-background px-6 text-center">
        <div>
          <h2 className="text-base font-semibold text-foreground">Workspace unavailable</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Sign in with an organization account to continue.
          </p>
        </div>
      </div>
    );
  }

  const approveWorkspaceAction = async (approval: BridgetteWorkspaceApproval) => {
    await execute(
      { type: approval.type, parameters: approval.parameters },
      `I'm ready to create this from ${selectedFile?.file_name ?? "the document"}.`,
    );
  };

  return (
    <>
      <BridgetteApprovalDialog pending={pendingApproval} onApprove={approve} onCancel={cancel} />
      <CanvasShell>
        {view === "home" && <HomeCanvas orgId={orgId} onSuggest={handleCommand} />}

        {view === "conversation" && (
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => ws.newSession()}
                className="gap-2 text-muted-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> New chat
              </Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,1fr)]">
              <div className="min-h-[60vh] overflow-hidden rounded-xl border border-border bg-card">
                <ConversationPane
                  messages={ws.messages}
                  files={ws.files}
                  selectedFile={selectedFile}
                  selectedFileId={selectedFile?.id ?? null}
                  isStreaming={ws.isStreaming}
                  onSend={ws.sendMessage}
                  onNewSession={() => ws.newSession()}
                  onUpload={ws.uploadFile}
                  onSelectFile={(file) => setSelectedFileId(file.id)}
                  onVoiceOpen={() => window.dispatchEvent(new CustomEvent("bridgette-open"))}
                  disabled={!ws.workspaceReady}
                  status={ws.workspaceStatus}
                />
              </div>
              {ws.files.length > 0 && (
                <div className="min-h-[60vh] overflow-hidden rounded-xl border border-border bg-card">
                  <WorkspaceReviewCanvas
                    files={ws.files}
                    selectedFile={selectedFile}
                    onQuickAction={ws.sendMessage}
                    disabled={ws.isStreaming}
                    embedded
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </CanvasShell>
    </>
  );
}
